import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Stack,
  Card,
  CardContent,
  Chip,
  Skeleton,
  Alert,
  Button,
  Avatar,
  Snackbar,
} from '@mui/material';
import MedicalInformationOutlinedIcon from '@mui/icons-material/MedicalInformationOutlined';
import Container from '@/components/PageSheet';
import { consultationsApi } from '@/services/api';
import { localized } from '@/utils/localizedContent';

/**
 * RULE 4 — the client chooses their doctor AFTER paying.
 *
 * Reached from the checkout return screen and from any consultation still in
 * `awaiting_physician`. Choosing is what issues the face-capture link, so this
 * screen gates the whole rest of the flow — hence the sticky confirm bar, and
 * the dashboard nag until it's done.
 *
 * Shell and card idiom follow ConsultationsPage: PageSheet container, outlined
 * elevation-0 cards, ButtonBase for tap targets.
 */
export default function PickPhysicianPage() {
  const { consultationId } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const qc = useQueryClient();
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState('');

  const consultationQuery = useQuery({
    queryKey: ['consultation', consultationId],
    queryFn: () => consultationsApi.getConsultation(consultationId),
  });
  const consultation = consultationQuery.data;

  const physiciansQuery = useQuery({
    queryKey: ['physicians', consultation?.serviceId, consultation?.clinicCode],
    queryFn: () =>
      consultationsApi.listPhysicians({
        serviceId: consultation?.serviceId,
        clinicCode: consultation?.clinicCode,
      }),
    enabled: Boolean(consultation),
  });
  const physicians = physiciansQuery.data || [];

  // "Choose for me" — most people don't want to pick a doctor from bios, and
  // asking them to is asking a patient to make a clinical judgement.
  const autoMut = useMutation({
    mutationFn: () => consultationsApi.choosePhysicianForMe(consultationId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-consultations'] });
      qc.invalidateQueries({ queryKey: ['consultation', consultationId] });
      // The doctor is chosen first, then what they will review.
      navigate(`/consultations/${encodeURIComponent(consultationId)}/choose-service`);
    },
    onError: (err) => {
      const code = err?.response?.data?.error?.code;
      if (code === 'NO_PHYSICIAN_AVAILABLE') return setError(t('pickPhysician.noneAvailable'));
      return setError(err?.response?.data?.error?.message || t('common.error'));
    },
  });

  const chooseMut = useMutation({
    mutationFn: (phone) => consultationsApi.choosePhysician(consultationId, phone),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-consultations'] });
      qc.invalidateQueries({ queryKey: ['consultation', consultationId] });
      navigate(`/consultations/${encodeURIComponent(consultationId)}/choose-service`);
    },
    onError: (err) => {
      const code = err?.response?.data?.error?.code;
      if (code === 'PHYSICIAN_UNAVAILABLE') {
        physiciansQuery.refetch();
        return setError(t('pickPhysician.unavailableNow'));
      }
      if (code === 'PHYSICIAN_LOCKED') return setError(t('pickPhysician.locked'));
      return setError(err?.response?.data?.error?.message || t('common.error'));
    },
  });

  // Mirrors PHYSICIAN_EDITABLE on the server. The pre-purchase statuses were
  // missing here, so a capture-first consultation — which is exactly what the
  // free stage produces — was told it had already chosen a doctor and could go
  // no further.
  const EDITABLE = ['draft_capture', 'awaiting_purchase', 'awaiting_physician', 'awaiting_capture'];

  // Past the point of choosing (e.g. the client hit "back") — don't offer a
  // second pick.
  if (consultation && !EDITABLE.includes(consultation.status)) {
    return (
      <Container maxWidth="sm" sx={{ py: { xs: 2, sm: 4 } }}>
        <Alert
          severity="info"
          action={
            <Button
              size="small"
              color="inherit"
              onClick={() => navigate(`/consultations/${encodeURIComponent(consultationId)}`)}
            >
              {t('pickPhysician.viewConsultation')}
            </Button>
          }
        >
          {t('pickPhysician.alreadyChosen')}
        </Alert>
      </Container>
    );
  }

  const loading = consultationQuery.isLoading || physiciansQuery.isLoading;

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 2, sm: 4 } }}>
      <Typography variant="h6" fontWeight={800}>
        {t('pickPhysician.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {consultation?.serviceName
          ? t('pickPhysician.subtitle', {
              service: localized(consultation.serviceName, lang) || '',
            })
          : t('pickPhysician.subtitleNoService')}
      </Typography>

      <Alert severity="info" sx={{ mb: 2, borderRadius: 3 }}>
        {t('pickPhysician.whatHappensNext')}
      </Alert>

      <Card
        elevation={0}
        sx={{ mb: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}
      >
        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography fontWeight={800}>{t('pickPhysician.autoTitle')}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t('pickPhysician.autoBody')}
            </Typography>
          </Box>
          <Button variant="contained" disabled={autoMut.isPending}
            onClick={() => autoMut.mutate()} sx={{ flex: 'none' }}>
            {autoMut.isPending ? t('common.loading') : t('pickPhysician.autoCta')}
          </Button>
        </CardContent>
      </Card>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {t('pickPhysician.orBrowse')}
      </Typography>

      {loading ? (
        <Stack spacing={1.5}>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} variant="rounded" height={110} />
          ))}
        </Stack>
      ) : physicians.length === 0 ? (
        <Card
          elevation={0}
          sx={{
            borderRadius: 3,
            border: '1px dashed',
            borderColor: 'divider',
            textAlign: 'center',
            py: 5,
          }}
        >
          <MedicalInformationOutlinedIcon sx={{ fontSize: 42, color: 'text.disabled', mb: 1 }} />
          <Typography fontWeight={800}>{t('pickPhysician.emptyTitle')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('pickPhysician.emptyBody')}
          </Typography>
        </Card>
      ) : (
        <Stack spacing={1.5}>
          {physicians.map((p) => {
            const bio = localized(p.bio, lang);
            const pending = chooseMut.isPending && selected === p.phone;
            return (
              <Card
                key={p.phone}
                elevation={0}
                sx={{
                  borderRadius: 3,
                  width: '100%',
                  border: '1px solid',
                  borderColor: 'divider',
                  opacity: p.available ? 1 : 0.55,
                }}
              >
                <CardContent>
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <Avatar src={p.avatarUrl || undefined} sx={{ width: 48, height: 48 }}>
                      {(p.fullName || '?').charAt(0)}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography fontWeight={800} noWrap>{p.fullName}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {localized(p.title, lang)}
                      </Typography>
                      {bio && <Typography variant="body2" sx={{ mt: 0.75 }}>{bio}</Typography>}
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                        {(p.specialties || []).map((sp) => (
                          <Chip key={sp} size="small" label={t(`categories.${sp}`, sp)} />
                        ))}
                      </Stack>
                      {!p.available && (
                        <Chip
                          size="small" variant="outlined" sx={{ mt: 1 }}
                          label={t(`pickPhysician.reason.${p.unavailableReason}`,
                                   t('pickPhysician.reason.NOT_ACCEPTING'))}
                        />
                      )}
                    </Box>
                  </Stack>

                  {/* One button per doctor, committing the choice directly. A
                      radio plus a confirm bar made choosing a two-step act and
                      hid the action off the card it belonged to. */}
                  <Button
                    fullWidth
                    variant="contained"
                    size="large"
                    sx={{ mt: 2 }}
                    disabled={!p.available || chooseMut.isPending}
                    onClick={() => { setSelected(p.phone); chooseMut.mutate(p.phone); }}
                  >
                    {pending ? t('common.loading') : t('pickPhysician.select')}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}

      <Snackbar
        open={Boolean(error)}
        autoHideDuration={5000}
        message={error}
        onClose={() => setError('')}
      />
    </Container>
  );
}
